<template>
    <div class="modal fade" :id="modalId" data-bs-backdrop="static" aria-labelledby="proposal-modal-title" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-scrollable">
            <div class="modal-content">
                <div class="modal-header border-bottom-0 pb-0">
                    <div class="d-flex align-items-start w-100">
                        <div class="flex-grow-1" style="min-width: 0; margin-right: 1rem;">
                            <h3 class="mt-2 mb-2 text-center text-sm-start">
                                Column Mapping Suggestion
                            </h3>
                        </div>
                        <div class="d-flex align-items-center" style="flex: 0 0 auto;">
                            <button type="button" class="btn btn-white close-modal" data-bs-dismiss="modal"
                                data-bs-toggle="tooltip" title="Close"
                                @click="handleClose">
                                <img class="icon me-0" src="/img/icons/avid-icons/close.svg" alt="">
                            </button>
                        </div>
                    </div>
                </div>
                <div class="modal-body">
                    <div v-if="loading" class="text-center py-4">
                        <div class="spinner-border text-primary" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <p class="mt-2">Loading suggestion...</p>
                    </div>

                    <div v-else-if="error" class="alert alert-danger">
                        {{ error }}
                    </div>

                    <div v-else class="d-flex flex-column gap-3">
                        <!-- Top section: Destination + Proposed Source Column -->
                        <div class="card segment-proposal-card">
                            <div class="card-body p-3">
                                <h4 class="h6 fw-semibold mb-3">Column Mapping</h4>
                                <div class="mb-3">
                                    <label class="form-label">Destination Column</label>
                                    <div class="form-control bg-light border-0" readonly>
                                        {{ formatDestinationColumn(columnMapping?.display_name || columnMapping?.destination_column || destinationColumn) }}
                                    </div>
                                </div>
                                <div>
                                    <label class="form-label">Proposed Source Column</label>
                                    <div v-if="(proposal || proposalData)?.column_logic == null">
                                        <div class="form-control bg-light border-0" readonly>
                                            {{ formatSourceColumn((proposal || proposalData)?.source_column) }}
                                        </div>
                                    </div>
                                    <div v-else>
                                        <p class="mb-2 fw-semibold">Custom Logic</p>
                                        <div v-if="formattedLogicDescription" class="formatted-logic p-2 bg-light rounded" style="max-height: 200px; overflow-y: auto; white-space: pre-line;" v-html="formattedLogicDescription"></div>
                                        <div v-else-if="(proposal || proposalData)?.column_logic" class="formatted-logic p-2 bg-light rounded" style="max-height: 200px; overflow-y: auto; white-space: pre-line;" v-html="formatColumnLogicForDisplay((proposal || proposalData).column_logic)"></div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Reasoning card - always visible when present -->
                        <div v-if="(proposal || proposalData)?.reasoning || reasoning" class="card segment-proposal-card">
                            <div class="card-body p-3">
                                <h4 class="h6 fw-semibold mb-3"><img class="icon me-2" src="/img/icons/avid-icons/edna.svg" alt=""> Reasoning</h4>
                                <div class="formatted-logic" style="white-space: pre-line;">{{ (proposal || proposalData)?.reasoning || reasoning }}</div>
                            </div>
                        </div>

                        <!-- Preview Output - card with collapsed accordion (always show when we have a proposal) -->
                        <div v-if="proposal || proposalData" class="card segment-proposal-card">
                            <div class="accordion accordion-flush" :id="`${modalId}-accordion-preview`">
                                <div class="accordion-item border-0">
                                    <h2 class="accordion-header">
                                        <button class="accordion-button collapsed py-3 h6 fw-semibold mb-0" type="button" data-bs-toggle="collapse" :data-bs-target="`#${modalId}-collapse-preview`" :aria-expanded="false" :aria-controls="`${modalId}-collapse-preview`">
                                            Preview Output (Top 10)
                                        </button>
                                    </h2>
                                    <div :id="`${modalId}-collapse-preview`" class="accordion-collapse collapse" :data-bs-parent="`#${modalId}-accordion-preview`">
                                        <div class="accordion-body p-0">
                                            <div v-if="samples && samples.length > 0" class="table-card table-width-responsive overflow-auto" style="max-height: 300px;">
                                                <table class="table table-without-pagination gy-0 m-0 bg-white">
                                                    <thead>
                                                        <tr>
                                                            <th class="ps-3 py-2">Value</th>
                                                            <th class="py-2 ps-2">Records</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr v-for="(sample, index) in samples" :key="index">
                                                            <td class="ps-3 align-middle py-3">{{ sample.name }}</td>
                                                            <td class="align-middle ps-2 py-3">{{ formatSampleCount(sample.count) }}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                            <div v-else class="p-3 text-muted small">
                                                No preview available for this mapping.
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
                <div class="modal-footer pt-1">
                    <button 
                        v-if="!loading && !error"
                        type="button" 
                        class="btn btn-secondary" 
                        @click="submitFeedback"
                        :disabled="submitting"
                    >
                        <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                        <img v-else class="icon me-2" src="/img/icons/avid-icons/edna.svg" alt="">
                        Refine
                    </button>
                    <button 
                        v-if="!loading && !error && (proposal || proposalData)"
                        type="button" 
                        class="btn btn-primary reg-btn" 
                        @click="acceptProposal"
                        :disabled="submitting"
                    >
                        <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                        Accept
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Swal from 'sweetalert2';
import { defineComponent } from 'vue';
import { useNotifications } from '../../composables/useNotifications';

export default defineComponent({
    name: 'ColumnMappingProposalModal',
    props: {
        client: {
            type: Object,
            required: true,
        },
        dataSetId: {
            type: Number,
            default: null,
        },
        columnMappingId: {
            type: Number,
            default: null,
        },
        columnMappingSuggestionId: {
            type: Number,
            default: null,
        },
        fileId: {
            type: Number,
            default: null,
        },
        destinationColumn: {
            type: String,
            required: true,
        },
        previousResponseId: {
            type: String,
            default: null,
        },
        proposal: {
            type: Object,
            default: null,
        },
        reasoning: {
            type: String,
            default: null,
        },
        source: {
            type: String,
            default: 'data_set',
        },
        dataSourceId: {
            type: [Number, String],
            default: null,
        },
    },
    data() {
        return {
            modalId: `proposal-modal-${Date.now()}`,
            loading: true,
            error: null,
            proposalData: null, // Store proposal from prop or loaded data
            columnMapping: null,
            samples: [],
            feedback: '',
            submitting: false,
            formattedLogicDescription: null,
        };
    },
    mounted() {
        // Initialize notifications composable
        const { showNotification } = useNotifications();
        this.showNotification = showNotification;
        this.loadProposal();
    },
    methods: {
        async loadProposal() {
            try {
                // Check if this is a file-based proposal (has fileId or source is 'file')
                if (this.fileId || this.source === 'file') {
                    // For files, use the proposal prop if provided
                    const proposalProp = this.$props.proposal;
                    if (proposalProp) {
                        // Store proposal from prop in data for consistency
                        this.proposalData = proposalProp;
                        this.columnMapping = {
                            destination_column: this.destinationColumn,
                            display_name: this.destinationColumn,
                        };
                        
                        // Get formatted logic description if needed
                        const proposal = this.proposal || this.proposalData;
                        if (proposal?.column_logic) {
                            await this.formatLogicDescription();
                        }
                        
                        // If we have a source column or logic, fetch sample results
                        if (proposal?.source_column || proposal?.column_logic) {
                            await this.loadSamples();
                        }
                        
                        this.loading = false;
                        return;
                    } else {
                        // Proposal not provided for file
                        this.error = 'Proposal data not available';
                        this.loading = false;
                        return;
                    }
                }
                
                // Load by suggestion ID (suggestion-only flow, no column definition yet)
                if (this.columnMappingSuggestionId && !this.columnMappingId) {
                    const response = await window.axios.get(`/api/column-mapping/${this.client.url}/suggestion/${this.columnMappingSuggestionId}`);
                    this.columnMapping = response.data.column_mapping;
                    const metadata = this.columnMapping?.ai_suggestion_metadata || {};
                    this.proposalData = metadata.proposal || { output_text: metadata.output_text || 'No proposal available.' };
                    if (response.data.formatted_logic_description) {
                        this.formattedLogicDescription = response.data.formatted_logic_description;
                    } else if (this.proposalData?.column_logic) {
                        await this.formatLogicDescription();
                    }
                    if (this.proposalData?.source_column || this.proposalData?.column_logic) {
                        await this.loadSamples();
                    }
                    this.loading = false;
                    return;
                }

                // Load from column definition (for datasets with existing column definition)
                if (!this.columnMappingId) {
                    this.error = 'Missing column definition ID or suggestion ID';
                    this.loading = false;
                    return;
                }

                const response = await window.axios.get(`/api/column-mapping/${this.client.url}/${this.columnMappingId}`);
                this.columnMapping = response.data.column_mapping;
                
                // Extract proposal from metadata
                const metadata = this.columnMapping?.ai_suggestion_metadata || {};
                this.proposalData = metadata.proposal || {
                    output_text: metadata.output_text || 'No proposal available.',
                };

                // Get formatted logic description if available
                const proposal = this.proposal || this.proposalData;
                if (response.data.formatted_logic_description) {
                    this.formattedLogicDescription = response.data.formatted_logic_description;
                } else if (proposal?.column_logic) {
                    // Fallback: format on frontend if backend didn't provide it
                    await this.formatLogicDescription();
                }

                // If we have a source column or logic, fetch sample results
                if (proposal?.source_column || proposal?.column_logic) {
                    await this.loadSamples();
                }

                this.loading = false;
            } catch (error) {
                console.error('Error loading proposal:', error);
                this.error = 'Failed to load proposal. Please try again.';
                this.loading = false;
            }
        },
        async formatLogicDescription() {
            const proposal = this.proposal || this.proposalData;
            if (!proposal?.column_logic) {
                return;
            }

            try {
                const response = await window.axios.post(`/api/column-mapping/${this.client.url}/format-logic`, {
                    column_logic: proposal.column_logic,
                });
                this.formattedLogicDescription = response.data.description;
            } catch (error) {
                console.error('Error formatting logic description:', error);
                // Fall back to JSON display
            }
        },
        async loadSamples() {
            try {
                const proposal = this.proposal || this.proposalData;
                const data = {
                    client: this.client,
                    source: this.source || 'data_set',
                    column: proposal?.source_column || 'CUSTOM',
                    logic: proposal?.column_logic || null,
                    limit: 10,
                    data_source_id: this.dataSourceId || this.dataSetId || this.fileId,
                    value: null,
                    data_type: this.columnMapping?.data_type || 'string',
                };

                const response = await window.axios.post('/api/bigquery/get_samples', data);
                this.samples = response.data.samples || [];
            } catch (error) {
                console.error('Error loading samples:', error);
                // Don't show error for samples, just leave empty
            }
        },
        formatColumnLogic(logic) {
            if (!logic) return 'No logic defined';
            return JSON.stringify(logic, null, 2);
        },
        formatSourceColumn(sourceColumn) {
            if (!sourceColumn) return 'Custom';
            return sourceColumn
                .replace(/_/g, ' ')
                .replace(/\./g, ' > ')
                .replace(/\b\w/g, char => char.toUpperCase());
        },
        formatDestinationColumn(destinationColumn) {
            if (!destinationColumn) return '';
            return destinationColumn
                .replace(/_/g, ' ')
                .replace(/\b\w/g, char => char.toUpperCase());
        },
        formatSampleCount(count) {
            if (count == null || count === undefined) {
                return '0';
            }
            return Number(count).toLocaleString();
        },
        formatColumnLogicForDisplay(logic) {
            if (!logic || !logic.units || !Array.isArray(logic.units)) {
                return 'No logic defined';
            }

            const descriptions = [];

            // Format each unit
            for (const unit of logic.units) {
                const outputValue = this.formatOutputValue(unit.value, unit.value_type);
                const conditionDescription = this.formatLogicConditions(unit.logic || {});

                if (conditionDescription) {
                    descriptions.push(`When ${conditionDescription}, output: ${outputValue}`);
                } else {
                    descriptions.push(`Output: ${outputValue}`);
                }
            }

            // Add default case
            const defaultDescription = this.formatDefaultValue(logic);
            if (defaultDescription) {
                descriptions.push(`Otherwise: ${defaultDescription}`);
            }

            // Return as HTML with line breaks (descriptions already contain HTML from formatLogicConditions)
            return descriptions.join('<br>');
        },
        formatLogicConditions(logic) {
            if (!logic.logic_units || !Array.isArray(logic.logic_units) || logic.logic_units.length === 0) {
                return '';
            }

            const logicUnits = logic.logic_units;
            let filterLogic = logic.filter_logic || '1';

            // Build descriptions for each logic unit
            const descriptions = {};
            logicUnits.forEach((unit, index) => {
                descriptions[index + 1] = this.formatLogicUnit(unit);
            });

            // Replace numbers in filter_logic with descriptions
            for (let i = 1; i <= logicUnits.length; i++) {
                filterLogic = filterLogic.replace(new RegExp(`\\b${i}\\b`, 'g'), `[${i}]`);
            }

            Object.keys(descriptions).forEach(key => {
                filterLogic = filterLogic.replace(new RegExp(`\\[${key}\\]`, 'g'), descriptions[key]);
            });

            return filterLogic;
        },
        formatLogicUnit(unit) {
            const dbColumn = unit.db_column?.text || 'Unknown Column';
            const columnName = unit.db_column?.category ? `${unit.db_column.category}: ${dbColumn}` : dbColumn;
            const operator = unit.operator?.value || '=';
            const operatorText = this.formatOperator(operator);
            const value = unit.value;

            if (operator === 'IS NULL' || operator === 'IS NOT NULL') {
                return `<span class="badge bg-secondary">${columnName}</span> ${operator === 'IS NULL' ? 'is blank' : 'is not blank'}`;
            }

            const formattedValue = this.formatValue(value, operator, unit.data_type);
            return `<span class="badge bg-secondary">${columnName}</span> ${operatorText} ${formattedValue}`;
        },
        formatOperator(operator) {
            const operators = {
                '=': 'equals',
                '<>': 'does not equal',
                'LIKE': 'contains',
                'NOT LIKE': 'does not contain',
                'IS NULL': 'is blank',
                'IS NOT NULL': 'is not blank',
                '>': 'is greater than',
                '<': 'is less than',
                '>=': 'is greater than or equal to',
                '<=': 'is less than or equal to',
                'IN': 'is one of',
                'NOT IN': 'is not one of',
            };
            return operators[operator] || operator;
        },
        formatValue(value, operator, dataType) {
            if (value === null || value === undefined) {
                return '';
            }

            // Handle IN/NOT IN operators with arrays
            if ((operator === 'IN' || operator === 'NOT IN') && Array.isArray(value)) {
                const total = value.length;
                const displayCount = Math.min(3, total);
                let formatted = '';

                for (let i = 0; i < displayCount; i++) {
                    if (value[i] !== undefined) {
                        const val = Array.isArray(value[i]) ? (value[i].text || value[i].value || '') : value[i];
                        if (val) {
                            formatted += (i === displayCount - 1 && displayCount > 1 ? 'or ' : '') + `"${val}", `;
                        }
                    }
                }

                if (total >= 10) {
                    const remaining = total - 3;
                    formatted += `or ${remaining.toLocaleString()} other options`;
                } else {
                    formatted = formatted.replace(/,\s*$/, '');
                }

                return formatted;
            }

            // Handle arrays (for multi-select values)
            if (Array.isArray(value)) {
                if (value.length === 1) {
                    const val = Array.isArray(value[0]) ? (value[0].text || value[0].value || '') : value[0];
                    return `"${val}"`;
                }

                const total = value.length;
                const displayCount = Math.min(3, total);
                let txt = '';

                for (let i = 0; i < displayCount; i++) {
                    const val = Array.isArray(value[i]) ? (value[i].text || value[i].value || '') : value[i];
                    if (val) {
                        txt += `"${val}", `;
                    }
                }

                if (total >= 10) {
                    const remaining = total - 3;
                    txt += `or ${remaining.toLocaleString()} other options`;
                } else {
                    txt = txt.replace(/,\s*$/, '');
                }

                return txt;
            }

            // Handle dates
            if (['date', 'datetime', 'timestamp'].includes(dataType) && typeof value === 'string') {
                if (value.includes('DATE_SUB') || value.includes('DATE_ADD')) {
                    const match = value.match(/INTERVAL\s+(\d+)\s+(\w+)/);
                    if (match && match.length === 3) {
                        const number = match[1];
                        const unit = match[2].toLowerCase().replace(/s$/, '');
                        return `${number} ${unit}${number != 1 ? 's' : ''} ago`;
                    }
                } else {
                    try {
                        const date = new Date(value);
                        return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' });
                    } catch (e) {
                        return `"${value}"`;
                    }
                }
            }

            // Default: quote strings
            if (typeof value === 'string' && value) {
                return `"${value}"`;
            }

            return String(value);
        },
        formatOutputValue(value, valueType) {
            if (valueType === 'static') {
                if (typeof value === 'boolean') {
                    return value ? 'Yes' : 'No';
                }
                if (typeof value === 'string' && value) {
                    return `"${value}"`;
                }
                return String(value);
            }

            if (valueType === 'column') {
                if (Array.isArray(value)) {
                    return value.text || value.value || 'column value';
                }
                return value || 'column value';
            }

            if (valueType === 'sql') {
                return 'SQL expression';
            }

            return String(value);
        },
        formatDefaultValue(columnLogic) {
            const defaultType = columnLogic.default_type || 'static';
            const defaultValue = columnLogic.default_value || '';

            if (defaultType === 'static') {
                if (typeof defaultValue === 'boolean') {
                    return defaultValue ? 'Yes' : 'No';
                }
                if (typeof defaultValue === 'string' && defaultValue) {
                    return `"${defaultValue}"`;
                }
                if (!defaultValue) {
                    return 'null';
                }
                return String(defaultValue);
            }

            if (defaultType === 'column') {
                if (Array.isArray(defaultValue)) {
                    return defaultValue.text || defaultValue.value || 'column value';
                }
                return defaultValue || 'column value';
            }

            if (defaultType === 'sql') {
                return 'SQL expression';
            }

            return 'null';
        },
        async acceptProposal() {
            const proposal = this.proposal || this.proposalData;
            if (!proposal) {
                this.showNotification({
                    text: 'No suggestions were found for this column.',
                    type: 'error'
                });
                return;
            }

            // For files, emit the proposal directly to parent component
            if (this.source === 'file' || this.fileId) {
                let source_column = null;
                let column_logic = null;

                if (proposal.column_logic) {
                    source_column = 'CUSTOM';
                    column_logic = proposal.column_logic;
                } else if (proposal.source_column) {
                    source_column = proposal.source_column;
                }

                // Close modal
                const modal = bootstrap.Modal.getInstance(document.getElementById(this.modalId));
                if (modal) {
                    modal.hide();
                }

                // Clear the pending action
                if (window.__pendingFileMappingProposal) {
                    delete window.__pendingFileMappingProposal;
                }

                // Emit the proposal data for parent to apply
                this.$emit('accepted', {
                    file_id: this.fileId,
                    destination_column: this.destinationColumn,
                    source_column: source_column,
                    column_logic: column_logic,
                    response_id: this.previousResponseId,
                });
                return;
            }

            // For datasets, use the API endpoint (column_mapping_id or column_mapping_suggestion_id required)
            if (!this.columnMappingId && !this.columnMappingSuggestionId) {
                this.showNotification({
                    text: 'Missing column mapping ID or suggestion ID.',
                    type: 'error'
                });
                return;
            }

            const data = {};
            if (this.columnMappingId) {
                data.column_mapping_id = this.columnMappingId;
            }
            if (this.columnMappingSuggestionId) {
                data.column_mapping_suggestion_id = this.columnMappingSuggestionId;
            }

            let source_column = null;
            let column_logic = null;

            if (proposal.column_logic) {
                // If there's custom logic, source_column must be 'CUSTOM'
                source_column = 'CUSTOM';
                column_logic = proposal.column_logic;
                data.source_column = source_column;
                data.column_logic = column_logic;
            } else if (proposal.source_column) {
                source_column = proposal.source_column;
                data.source_column = source_column;
            }

            if (this.columnMapping?.ai_suggestion_metadata?.response_id) {
                data.response_id = this.columnMapping.ai_suggestion_metadata.response_id;
            }
            if (this.columnMapping?.column_mapping_suggestion_id) {
                data.column_mapping_suggestion_id = this.columnMapping.column_mapping_suggestion_id;
            }

            this.submitting = true;
            try {
                const response = await window.axios.post(`/api/column-mapping/${this.client.url}/accept`, data);
                const acceptedColumnMapping = response.data?.column_mapping;

                const modal = bootstrap.Modal.getInstance(document.getElementById(this.modalId));
                if (modal) {
                    modal.hide();
                }
                if (window.__pendingColumnMappingProposal) {
                    delete window.__pendingColumnMappingProposal;
                }

                this.$emit('accepted', {
                    column_mapping_id: acceptedColumnMapping?.id ?? this.columnMappingId,
                    column_mapping_suggestion_id: this.columnMappingSuggestionId,
                    destination_column: this.destinationColumn,
                    source_column: source_column,
                    column_logic: column_logic,
                });
            } catch (error) {
                console.error('Error accepting proposal:', error);
                this.showNotification({
                    text: error.response?.data?.message || 'Failed to accept. Please try again.',
                    type: 'error'
                });
            } finally {
                this.submitting = false;
            }
        },
        async submitFeedback() {
            // Always prompt for user context via Swal
            Swal.fire({
                title: 'Refine Column Mapping Suggestion',
                // Important: Bootstrap modals trap focus. Render the SweetAlert inside the modal
                // so the textarea can receive focus/clicks.
                target: document.getElementById(this.modalId),
                html: `
                    <p class="mb-2">How can we improve the current column mapping suggestion?</p>
                    <textarea id="column-context" class="form-control" placeholder="E.g., Can you try using X column instead? Or, I don't want Y to be a possible output value." style="width: 100%; min-height: 100px;">${this.feedback || ''}</textarea>
                `,
                showCancelButton: true,
                confirmButtonText: 'Submit',
                cancelButtonText: 'Cancel',
                didOpen: () => {
                    const textarea = Swal.getPopup()?.querySelector('#column-context');
                    if (textarea) {
                        textarea.focus();
                    }
                },
                preConfirm: () => {
                    const context = document.getElementById('column-context').value;
                    if (!context || !context.trim()) {
                        Swal.showValidationMessage('Please provide feedback to continue the conversation.');
                        return false;
                    }
                    return context || null;
                }
            }).then(async (result) => {
                if (result.isConfirmed) {
                    const userContext = result.value || '';
                    
                    this.submitting = true;

                    try {
                        const data = {
                            data_set_id: this.dataSetId,
                            column_mapping_id: this.columnMappingId,
                            destination_column: this.destinationColumn,
                            feedback: userContext,
                            previous_response_id: this.columnMapping?.ai_suggestion_metadata?.response_id || this.previousResponseId,
                        };
                        if (this.columnMapping?.column_mapping_suggestion_id) {
                            data.column_mapping_suggestion_id = this.columnMapping.column_mapping_suggestion_id;
                        }

                        await window.axios.post(`/api/column-mapping/${this.client.url}/feedback`, data);

                        this.showNotification({
                            icon: '/img/icons/nav-icons/Edna.svg',
                            text: 'We are refining the suggestions based on your feedback. You\'ll be notified when it\'s ready!',
                            type: 'info'
                        });

                        // Close modal
                        const modal = bootstrap.Modal.getInstance(document.getElementById(this.modalId));
                        if (modal) {
                            modal.hide();
                        }
                        // Clear the pending action when modal is closed
                        if (window.__pendingColumnMappingProposal) {
                            delete window.__pendingColumnMappingProposal;
                        }
                    } catch (error) {
                        console.error('Error submitting feedback:', error);
                        this.showNotification({
                            text: error.response?.data?.message || 'Failed to submit feedback. Please try again.',
                            type: 'error'
                        });
                    } finally {
                        this.submitting = false;
                    }
                }
            });
        },
        show() {
            // Method to programmatically show the modal
            const modalElement = document.getElementById(this.modalId);
            if (modalElement && window.bootstrap) {
                let modal = window.bootstrap.Modal.getInstance(modalElement);
                if (!modal) {
                    modal = new window.bootstrap.Modal(modalElement);
                }
                modal.show();
            }
        },
        handleClose() {
            // Clear the pending action when modal is closed
            if (window.__pendingColumnMappingProposal) {
                delete window.__pendingColumnMappingProposal;
            }
        },
    },
});
</script>